/* eslint-disable no-param-reassign  */
import { createSlice } from '@reduxjs/toolkit';
import { concat, append, assocPath, pathOr } from 'ramda';
import { useDispatch } from 'react-redux';
import { FETCH_STATUSES } from 'utils/fetchStatusUtils';
import LoanOfficerRealEstateAgentsRepository from 'repositories/loanOfficer/RealEstateAgentsRepository';
import WaitlistRealEstateAgentLoanOfficersRepository from 'repositories/loanOfficer/WaitlistRealEstateAgentLoanOfficersRepository';

const initialState = {
  realEstateAgents: [],
  realEstateAgentsLoadingStatus: FETCH_STATUSES.idle,
  realEstateAgentsLoadingMoreStatus: FETCH_STATUSES.idle,
  meta: {},

  waitlistRealEstateAgents: [],
  waitlistRealEstateAgentsLoadingStatus: FETCH_STATUSES.idle,
  waitlistRealEstateAgentsLoadingMoreStatus: FETCH_STATUSES.idle,
  waitlistRealEstateAgentsMeta: {},

  inFavoritesRealEstateAgents: [],
  inFavoritesRealEstateAgentsLoadingStatus: FETCH_STATUSES.idle,
  isFavoritesRealEstateAgentsLoadingMoreStatus: FETCH_STATUSES.idle,
  inFavoritesRealEstateAgentsMeta: {},

  addingToWaitlistStatus: FETCH_STATUSES.idle,
};

const loanOfficerRealEstateAgentsSlice = createSlice({
  name: 'loanOfficerRealEstateAgentsSlice',
  initialState,
  reducers: {
    loadRealEstateAgentsStart(state) {
      state.realEstateAgentsLoadingStatus = FETCH_STATUSES.pending;
    },
    loadRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.realEstateAgents = realEstateAgents;
      state.meta = meta;
      state.realEstateAgentsLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    loadRealEstateAgentsFail(state) {
      state.realEstateAgentsLoadingStatus = FETCH_STATUSES.failed;
    },
    loadMoreRealEstateAgentsStart(state) {
      state.realEstateAgentsLoadingMoreStatus = FETCH_STATUSES.pending;
    },
    loadMoreRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.realEstateAgents = concat(state.realEstateAgents, realEstateAgents);
      state.meta = meta;
      state.realEstateAgentsLoadingMoreStatus = FETCH_STATUSES.fulfilled;
    },
    loadMoreRealEstateAgentsFail(state) {
      state.realEstateAgentsLoadingMoreStatus = FETCH_STATUSES.failed;
    },

    loadWaitlistRealEstateAgentsStart(state) {
      state.waitlistRealEstateAgentsLoadingStatus = FETCH_STATUSES.pending;
    },
    loadWaitlistRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.waitlistRealEstateAgents = realEstateAgents;
      state.waitlistRealEstateAgentsMeta = meta;
      state.waitlistRealEstateAgentsLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    loadWaitlistRealEstateAgentsFail(state) {
      state.waitlistRealEstateAgentsLoadingStatus = FETCH_STATUSES.failed;
    },
    loadMoreWaitlistRealEstateAgentsStart(state) {
      state.waitlistRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.pending;
    },
    loadMoreWaitlistRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.waitlistRealEstateAgents = concat(state.waitlistRealEstateAgents, realEstateAgents);
      state.waitlistRealEstateAgentsMeta = meta;
      state.waitlistRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.fulfilled;
    },
    loadMoreWaitlistRealEstateAgentsFail(state) {
      state.waitlistRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.failed;
    },

    loadInFavoritesRealEstateAgentsStart(state) {
      state.inFavoritesRealEstateAgentsLoadingStatus = FETCH_STATUSES.pending;
    },
    loadInFavoritesRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.inFavoritesRealEstateAgents = realEstateAgents;
      state.inFavoritesRealEstateAgentsMeta = meta;
      state.inFavoritesRealEstateAgentsLoadingStatus = FETCH_STATUSES.fulfilled;
    },
    loadInFavoritesRealEstateAgentsFail(state) {
      state.inFavoritesRealEstateAgentsLoadingStatus = FETCH_STATUSES.failed;
    },
    loadMoreInFavoritesRealEstateAgentsStart(state) {
      state.isFavoritesRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.pending;
    },
    loadMoreInFavoritesRealEstateAgentsSuccess(state, { payload }) {
      const { realEstateAgents, meta } = payload;
      state.inFavoritesRealEstateAgents = concat(state.inFavoritesRealEstateAgents, realEstateAgents);
      state.inFavoritesRealEstateAgentsMeta = meta;
      state.isFavoritesRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.fulfilled;
    },
    loadMoreInFavoritesRealEstateAgentsFail(state) {
      state.isFavoritesRealEstateAgentsLoadingMoreStatus = FETCH_STATUSES.failed;
    },

    addToWaitlistStart(state) {
      state.addingToWaitlistStatus = FETCH_STATUSES.pending;
    },
    addToWaitlistSuccess(state, { payload }) {
      const { realEstateAgent } = payload;
      const totalCount = pathOr(0, ['totalCount'], state.waitlistRealEstateAgentsMeta);
      const meta = assocPath(['totalCount'], totalCount + 1, state.waitlistRealEstateAgentsMeta);

      state.waitlistRealEstateAgentsMeta = meta;
      state.waitlistRealEstateAgents = append(realEstateAgent, state.waitlistRealEstateAgents);
      state.addingToWaitlistStatus = FETCH_STATUSES.fulfilled;
    },
    addToWaitlistFail(state) {
      state.addingToWaitlistStatus = FETCH_STATUSES.failed;
    },
  },
});

export const { actions } = loanOfficerRealEstateAgentsSlice;

export default loanOfficerRealEstateAgentsSlice.reducer;

export const useAppActions = () => {
  const dispatch = useDispatch();

  const loadRealEstateAgents = async params => {
    dispatch(actions.loadRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadRealEstateAgentsFail());
    }
  };

  const loadMoreRealEstateAgents = async params => {
    dispatch(actions.loadMoreRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadMoreRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadMoreRealEstateAgentsFail());
    }
  };

  const loadWaitlistRealEstateAgents = async params => {
    dispatch(actions.loadWaitlistRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadWaitlistRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadWaitlistRealEstateAgentsFail());
    }
  };

  const loadMoreWaitlistRealEstateAgents = async params => {
    dispatch(actions.loadMoreWaitlistRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadMoreWaitlistRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadMoreWaitlistRealEstateAgentsFail());
    }
  };

  const loadInFavoritesRealEstateAgents = async params => {
    dispatch(actions.loadInFavoritesRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadInFavoritesRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadInFavoritesRealEstateAgentsFail());
    }
  };

  const loadMoreInFavoritesRealEstateAgents = async params => {
    dispatch(actions.loadMoreInFavoritesRealEstateAgentsStart());
    try {
      const { realEstateAgents, meta } = await LoanOfficerRealEstateAgentsRepository.index(params);
      dispatch(actions.loadMoreInFavoritestRealEstateAgentsSuccess({ realEstateAgents, meta }));
    } finally {
      dispatch(actions.loadMoreInFavoritesRealEstateAgentsFail());
    }
  };

  const addToWaitlist = async params => {
    dispatch(actions.addToWaitlistStart());
    try {
      const { realEstateAgent } = await WaitlistRealEstateAgentLoanOfficersRepository.create(params);
      dispatch(actions.addToWaitlistSuccess({ realEstateAgent }));
    } finally {
      dispatch(actions.addToWaitlistFail());
    }
  };

  return {
    loadRealEstateAgents,
    loadMoreRealEstateAgents,
    loadWaitlistRealEstateAgents,
    loadMoreWaitlistRealEstateAgents,
    addToWaitlist,
    loadInFavoritesRealEstateAgents,
    loadMoreInFavoritesRealEstateAgents,
  };
};
/* eslint-enable no-param-reassign */



// WEBPACK FOOTER //
// ./src/reducers/slices/loanOfficer/realEstateAgentsSlice.js